import { Link } from 'react-router-dom';

import { Reveal } from '@/components/Reveal';
import { track } from '@/services/analytics';

import { useCollections } from './useCollections';
import { useGenerationOpen } from './useGenerationOpen';
import { useUpcomingCollections } from './useUpcomingCollections';

const CARD = 'rounded-card border border-border bg-surface p-4';

function Cover({ src, className = '' }: { src: string; className?: string }) {
  return <img src={src} alt="" className={`aspect-[3/4] w-full rounded-card object-cover ${className}`} />;
}

/**
 * Список коллекций на главной — первый шаг пользовательского пути.
 *
 * Карточка ведёт в мастер по адресу /create/<slug>. Ссылка, а не кнопка:
 * мастер конкретной коллекции — самостоятельный адрес, которым можно
 * поделиться и который переживает перезагрузку.
 *
 * Готовые коллекции — крупные карточки с обложкой. Анонсы «Скоро» идут
 * следом отдельным тихим рядом без ссылки (мастера у них ещё нет) и без
 * серых заглушек: пока обложек нет, это просто название и описание.
 */
export function CollectionsScreen() {
  const { data, isPending, isError, refetch } = useCollections();
  const upcoming = useUpcomingCollections();
  const generationOpen = useGenerationOpen();

  if (isPending) {
    return <p className="py-8 text-text-2">Загрузка коллекций…</p>;
  }

  if (isError) {
    return (
      <div className="py-8">
        <p className="text-danger">Не удалось загрузить коллекции. Попробуйте ещё раз.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-1 inline-flex min-h-11 items-center text-sm text-accent hover:underline"
        >
          Повторить
        </button>
      </div>
    );
  }

  if (data.length === 0 && upcoming.length === 0) {
    return <p className="py-8 text-text-2">Коллекции пока не добавлены.</p>;
  }

  return (
    <div className="flex flex-col gap-8 py-8">
      {data.length > 0 && (
        <div className="flex flex-col gap-4">
          {!generationOpen && (
            <p className="text-sm text-text-2">
              Создание портретов скоро откроется. Пока можно выбрать коллекцию и посмотреть параметры.
            </p>
          )}

          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((collection, index) => (
              <Reveal
                as="li"
                key={collection.id}
                order={index % 3}
                className={data.length === 1 ? 'sm:col-span-2 lg:col-span-3' : undefined}
              >
                <Link
                  to={`/create/${collection.slug}`}
                  onClick={() => track('collection_selected', { collection: collection.slug })}
                  className={`${CARD} flex h-full w-full flex-col gap-4 text-left hover:border-border-strong ${
                    data.length === 1 ? 'sm:flex-row sm:items-center sm:gap-6' : ''
                  }`}
                >
                  {collection.cover ? (
                    <Cover
                      src={collection.cover}
                      className={data.length === 1 ? 'sm:w-64 sm:shrink-0 lg:w-72' : ''}
                    />
                  ) : null}

                  <div>
                    <p className="font-medium text-text">{collection.title}</p>
                    <p className="mt-1 text-sm text-text-2">{collection.description}</p>
                    {!generationOpen && (
                      <span className="mt-3 inline-block rounded-full border border-border-strong px-2 py-0.5 text-xs text-text-2">
                        Создание скоро откроется
                      </span>
                    )}
                  </div>
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      )}

      {upcoming.length > 0 && (
        <section aria-labelledby="upcoming-heading" className="flex flex-col gap-4">
          <h3 id="upcoming-heading" className="text-lg font-semibold text-text">
            Скоро в студии
          </h3>

          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {upcoming.map((item, index) => (
              <Reveal as="li" key={item.id} order={index % 2}>
                <div className={`${CARD} flex h-full gap-4`} aria-label={`${item.title} — скоро`}>
                  {item.cover ? <Cover src={item.cover} className="w-16 shrink-0 opacity-70" /> : null}

                  <div>
                    <p className="flex flex-wrap items-center gap-2 font-medium text-text">
                      {item.title}
                      <span className="rounded-full border border-border-strong px-2 py-0.5 text-xs font-normal text-text-2">
                        Скоро
                      </span>
                    </p>
                    <p className="mt-1 text-sm text-text-2">{item.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
